// VRT search overlay controller. One <vrt-search> exists globally, opened by
// any [data-vrt-search-toggle] click (header search action + mobile drawer
// search row). Live results come from the Section Rendering API against
// Theme.routes.predictive_search_url (sections/vrt-search-results.liquid) —
// money/locale formatting stays in that Liquid fragment, JS only swaps HTML.

const SECTION_ID = 'vrt-search-results';
const MIN_CHARS = 2;
const DEBOUNCE_MS = 200;

class VrtSearch extends HTMLElement {
  connectedCallback() {
    this._input = this.querySelector('[data-vrt-search-input]');
    this._grid = this.querySelector('[data-vrt-search-grid]');
    this._hint = this.querySelector('[data-vrt-search-hint]');
    this._debounce = null;
    this._controller = null;

    this.addEventListener('click', (e) => {
      if (e.target.closest('[data-vrt-search-close]')) {
        e.preventDefault();
        this.close();
        return;
      }
      const pill = e.target.closest('[data-vrt-search-pill]');
      if (pill) {
        e.preventDefault();
        this._input.value = pill.dataset.term;
        this._input.focus();
        this.search(pill.dataset.term);
      }
    });

    this._input.addEventListener('input', () => {
      clearTimeout(this._debounce);
      const term = this._input.value.trim();
      this._debounce = setTimeout(() => this.search(term), DEBOUNCE_MS);
    });

    this._onDocClick = (e) => {
      const toggle = e.target.closest('[data-vrt-search-toggle]');
      if (toggle) {
        e.preventDefault();
        this.open();
      }
    };
    document.addEventListener('click', this._onDocClick);

    this._onKey = (e) => {
      if (e.key === 'Escape' && this.classList.contains('is-open')) this.close();
    };
    document.addEventListener('keydown', this._onKey);
  }

  disconnectedCallback() {
    document.removeEventListener('click', this._onDocClick);
    document.removeEventListener('keydown', this._onKey);
  }

  get panel() {
    return this.querySelector('.vrt-search__panel');
  }

  open() {
    this.classList.add('is-open');
    document.body.classList.add('vrt-no-scroll');
    if (this.panel) this.panel.setAttribute('aria-hidden', 'false');
    this._input.focus();
  }

  close() {
    this.classList.remove('is-open');
    document.body.classList.remove('vrt-no-scroll');
    if (this.panel) this.panel.setAttribute('aria-hidden', 'true');
  }

  async search(term) {
    if (this._controller) this._controller.abort();

    if (term.length < MIN_CHARS) {
      this._grid.innerHTML = '';
      if (this._hint) this._hint.hidden = false;
      return;
    }
    if (this._hint) this._hint.hidden = true;

    this._controller = new AbortController();
    const url = new URL(Theme.routes.predictive_search_url, location.origin);
    url.searchParams.set('q', term);
    url.searchParams.set('resources[type]', 'product');
    url.searchParams.set('resources[limit]', '6');
    url.searchParams.set('section_id', SECTION_ID);

    try {
      const res = await fetch(url, { signal: this._controller.signal });
      const html = await res.text();
      const fragment = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-vrt-search-fragment]');
      if (fragment) this._grid.innerHTML = fragment.innerHTML;
    } catch (err) {
      if (err.name !== 'AbortError') throw err;
    }
  }
}

if (!customElements.get('vrt-search')) {
  customElements.define('vrt-search', VrtSearch);
}
